import { DocumentPlusIcon, PlusIcon, QueueListIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, PencilSquareIcon } from '@heroicons/react/20/solid';
import { DragDropContext, DragStart, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { FormEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Button,
    Card,
    Chip,
    Field,
    FormActions,
    FormSection,
    InlineBanner,
    Input,
    LanguageTabs,
    LinkButton,
    PageHeader,
    Segmented,
    Skeleton,
    SkeletonRegion,
    StateBlock,
    Textarea,
    notify,
} from '../../components/ui';
import { OWNER_ABOVE } from '../../constants/roles';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useApiError } from '../../hooks/useApiError';
import { usePageTitle } from '../../hooks/usePageTitle';
import useAuth from '../../hooks/useAuth';
import type { RootState } from '../../store';
import { useAvailability } from '../MenuItems/hooks/useAvailability';
import { AddItemsDialog } from './components/AddItemsDialog';
import { CategoryCard } from './components/CategoryCard';
import { focusFirstInvalid, useFormErrors } from './hooks/useFormErrors';
import { useMenuForm } from './hooks/useMenuForm';
import { useUnsavedGuard } from './hooks/useUnsavedGuard';
import { categoryKey, nameKey, validateMenu } from './methods';

const EditorSkeleton = () => (
    <SkeletonRegion>
        <div className="grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
            <Card padding="lg" className="flex flex-col gap-4">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-24 w-full" />
            </Card>
            <div className="flex flex-col gap-4">
                {[0, 1].map((i) => (
                    <Card key={i} padding="lg" className="flex flex-col gap-4">
                        <Skeleton className="h-5 w-1/4" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-11 w-full" />
                    </Card>
                ))}
            </div>
        </div>
    </SkeletonRegion>
);

const MenuEditor = ({ menuId }: { menuId?: string }) => {
    const { t } = useTranslation('menu');
    const navigate = useNavigate();
    const apiError = useApiError();
    const { hasRole } = useAuth();
    const readOnly = !hasRole(OWNER_ABOVE);
    const previewLang = useSelector((s: RootState) => s.language.current);
    const { restaurants, restaurantId: activeRestaurantId, isAdmin } = useActiveLocation();

    const form = useMenuForm(menuId);
    const [dragFrom, setDragFrom] = useState<string | null>(null);
    const { state, languages, items, restaurant, actions } = form;
    const [lang, setLang] = useState<string | null>(null);
    const activeLang = lang && languages.includes(lang) ? lang : languages.includes(previewLang) ? previewLang : languages[0];

    const [addingTo, setAddingTo] = useState<string | null>(null);
    const [focusKey, setFocusKey] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [announcement, setAnnouncement] = useState('');
    const formRef = useRef<HTMLFormElement>(null);

    const guard = useUnsavedGuard(form.dirty && !readOnly);
    const catalogue = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
    const setAvailable = useAvailability((id, isAvailable) =>
        form.setItems((list) => list?.map((i) => (i.id === id ? { ...i, isAvailable } : i)) ?? list)
    );

    const errors = useMemo(() => (state ? validateMenu(state, languages, t) : {}), [state, languages, t]);
    const feedback = useFormErrors(errors);
    const incomplete = useMemo(
        () =>
            feedback.submitted
                ? languages.filter((l) => errors[nameKey(l)] || state?.categories.some((c) => errors[categoryKey(c.key, l)]))
                : [],
        [feedback.submitted, languages, errors, state]
    );

    const menuName = state?.names[activeLang]?.trim() ?? '';
    usePageTitle(menuId ? t('editor.titleEdit') : t('editor.titleNew'));

    const submit = async () => {
        if (!state || saving) return;
        if (!feedback.attempt()) {
            const firstBad = languages.find((l) => errors[nameKey(l)] || state.categories.some((c) => errors[categoryKey(c.key, l)]));
            if (firstBad) setLang(firstBad);
            focusFirstInvalid(formRef.current);
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            await form.save();
            guard.allowLeaving();
            notify.success(t('editor.saved', { name: menuName || t('editor.untitled') }));
            navigate('/menus');
        } catch (e) {
            const message = apiError(e);
            setSaveError(message);
            notify.error(message, { retry: { label: t('editor.retry'), run: () => void submitRef.current() } });
        } finally {
            setSaving(false);
        }
    };
    const submitRef = useRef(submit);
    submitRef.current = submit;

    const onDragStart = ({ type, source }: DragStart) => setDragFrom(type === 'ITEM' ? source.droppableId.replace('items:', '') : null);
    const onDragEnd = (result: DropResult) => {
        setDragFrom(null);
        const { source, destination } = result;
        if (!destination || !state) return;
        if (result.type === 'CATEGORY') {
            actions.moveCategory(source.index, destination.index);
            setAnnouncement(t('editor.movedTo', { position: destination.index + 1, total: state.categories.length }));
            return;
        }
        const from = source.droppableId.replace('items:', '');
        const to = destination.droppableId.replace('items:', '');
        actions.moveItem(from, source.index, to, destination.index);
    };

    const moveCategory = (index: number, delta: -1 | 1) => {
        if (!state) return;
        actions.moveCategory(index, index + delta);
        setAnnouncement(t('editor.movedTo', { position: index + delta + 1, total: state.categories.length }));
    };

    const addCategory = () => setFocusKey(actions.addCategory());

    const restaurantName = restaurants.find((r) => r.id === (restaurant?.id ?? activeRestaurantId))?.name ?? '';
    const addingCategory = state?.categories.find((c) => c.key === addingTo);

    const title = menuName || (menuId ? t('editor.untitled') : t('editor.titleNew'));

    return (
        <form
            ref={formRef}
            noValidate
            onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void submit();
            }}
        >
            <PageHeader
                title={title}
                back={{ to: '/menus', label: t('menus.title') }}
                actions={
                    state &&
                    (readOnly ? (
                        <Chip variant={state.isActive ? 'success' : 'neutral'} icon={state.isActive ? CheckCircleIcon : PencilSquareIcon} size="lg">
                            {t(state.isActive ? 'menus.active' : 'menus.draft')}
                        </Chip>
                    ) : (
                        <Segmented
                            aria-label={t('editor.status')}
                            value={state.isActive ? 'active' : 'draft'}
                            onChange={(v) => actions.setActive(v === 'active')}
                            options={[
                                { value: 'draft', label: t('menus.draft') },
                                { value: 'active', label: t('menus.active') },
                            ]}
                        />
                    ))
                }
            />

            <p role="status" className="sr-only">
                {announcement}
            </p>

            {form.status === 'error' && (
                <Card padding="none">
                    <StateBlock
                        variant={form.missing ? 'empty' : 'error'}
                        icon={QueueListIcon}
                        title={form.missing ? t('editor.notFound') : t('editor.loadFailed')}
                        body={form.missing ? t('editor.notFoundBody') : t('editor.loadFailedBody')}
                        action={
                            form.missing ? (
                                <LinkButton to="/menus" variant="outline">
                                    {t('menus.title')}
                                </LinkButton>
                            ) : (
                                <Button type="button" onClick={form.reload}>
                                    {t('editor.retry')}
                                </Button>
                            )
                        }
                    />
                </Card>
            )}
            {form.status === 'loading' && <EditorSkeleton />}

            {state && (
                <div className="flex flex-col gap-4">
                    {readOnly && <InlineBanner tone="info">{t('editor.readOnly')}</InlineBanner>}
                    {!readOnly && feedback.submitted && feedback.count > 0 && (
                        <InlineBanner tone="danger">{t('editor.fieldsNeedAttention', { count: feedback.count })}</InlineBanner>
                    )}
                    {!readOnly && saveError && (
                        <InlineBanner tone="danger" action={{ label: t('editor.retry'), onClick: () => void submit() }}>
                            {saveError}
                        </InlineBanner>
                    )}

                    {languages.length > 1 && (
                        <LanguageTabs languages={languages} value={activeLang} onChange={setLang} incomplete={incomplete} />
                    )}

                    <div className="grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start">
                        <div className="lg:sticky lg:top-20">
                            <FormSection title={t('editor.details')}>
                                {isAdmin && (
                                    <Field label={t('editor.restaurant')}>
                                        {(control) => <Input {...control} value={restaurantName} readOnly />}
                                    </Field>
                                )}
                                <Field label={t('editor.name')} error={feedback.shown(nameKey(activeLang))}>
                                    {(control) => (
                                        <Input
                                            {...control}
                                            lang={activeLang}
                                            value={state.names[activeLang] ?? ''}
                                            onChange={(e) => actions.setName(activeLang, e.target.value)}
                                            onBlur={() => feedback.blur(nameKey(activeLang))}
                                            disabled={readOnly}
                                            autoComplete="off"
                                        />
                                    )}
                                </Field>
                                <Field label={t('editor.description')} optional>
                                    {(control) => (
                                        <Textarea
                                            {...control}
                                            lang={activeLang}
                                            rows={3}
                                            value={state.descriptions[activeLang] ?? ''}
                                            onChange={(e) => actions.setDescription(activeLang, e.target.value)}
                                            disabled={readOnly}
                                        />
                                    )}
                                </Field>
                            </FormSection>
                        </div>

                        <section aria-labelledby="menu-categories" className="min-w-0">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <h2 id="menu-categories" className="text-title text-strong">
                                    {t('editor.categories')}
                                </h2>
                                {!readOnly && state.categories.length > 0 && (
                                    <Button type="button" variant="outline" icon={<PlusIcon className="size-5" aria-hidden="true" />} onClick={addCategory}>
                                        {t('editor.addCategory')}
                                    </Button>
                                )}
                            </div>

                            {state.categories.length === 0 ? (
                                <Card padding="none">
                                    <StateBlock
                                        variant="empty"
                                        icon={DocumentPlusIcon}
                                        title={t('editor.emptyTitle')}
                                        body={t('editor.emptyBody')}
                                        action={
                                            !readOnly && (
                                                <Button type="button" variant="strong" onClick={addCategory}>
                                                    {t('editor.addCategory')}
                                                </Button>
                                            )
                                        }
                                    />
                                </Card>
                            ) : (
                                <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd} dragHandleUsageInstructions={t('editor.dragInstructions')}>
                                    <Droppable droppableId="categories" type="CATEGORY" isDropDisabled={readOnly}>
                                        {(drop) => (
                                            <ul ref={drop.innerRef} {...drop.droppableProps} className="flex flex-col gap-4">
                                                {state.categories.map((category, index) => (
                                                    <Draggable key={category.key} draggableId={category.key} index={index} isDragDisabled={readOnly} disableInteractiveElementBlocking>
                                                        {(drag, snapshot) => (
                                                            <li ref={drag.innerRef} {...drag.draggableProps}>
                                                                <CategoryCard
                                                                    category={category}
                                                                    index={index}
                                                                    total={state.categories.length}
                                                                    lang={activeLang}
                                                                    readOnly={readOnly}
                                                                    dropBlocked={dragFrom === category.key}
                                                                    catalogue={catalogue}
                                                                    restaurant={restaurant}
                                                                    dragHandle={drag.dragHandleProps}
                                                                    dragging={snapshot.isDragging}
                                                                    error={feedback.shown(categoryKey(category.key, activeLang))}
                                                                    autoFocus={focusKey === category.key}
                                                                    onRename={(value) => actions.renameCategory(category.key, activeLang, value)}
                                                                    onBlurName={() => feedback.blur(categoryKey(category.key, activeLang))}
                                                                    onMove={(delta) => moveCategory(index, delta)}
                                                                    onDelete={() => actions.removeCategory(category.key)}
                                                                    onAddItems={() => setAddingTo(category.key)}
                                                                    onRemoveItem={(itemId) => actions.removeItem(category.key, itemId)}
                                                                    onToggleAvailable={(item, name, next) => void setAvailable(item.id, name, next)}
                                                                />
                                                            </li>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {drop.placeholder}
                                            </ul>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            )}
                        </section>
                    </div>

                    {!readOnly && (
                        <FormActions
                            onCancel={() => guard.leave('/menus')}
                            dirty={form.dirty}
                            submitting={saving}
                            submitLabel={t('editor.saveMenu')}
                        />
                    )}
                </div>
            )}

            <AddItemsDialog
                open={addingTo !== null}
                onClose={() => setAddingTo(null)}
                items={items}
                present={addingCategory?.itemIds ?? []}
                lang={activeLang}
                restaurant={restaurant}
                onAdd={(ids) => addingTo && actions.addItems(addingTo, ids)}
                createTo={menuId ? `/menu-items/create?returnTo=${encodeURIComponent(`/menus/edit/${menuId}`)}` : undefined}
            />
            {guard.dialog}
        </form>
    );
};

/** /menus/create, /menus/edit/:menuId and /menus/:id. Roles that cannot edit get the same screen read-only. */
const MenuForm = () => {
    const { menuId, id } = useParams();
    const current = menuId ?? id;
    return <MenuEditor key={current ?? 'new'} menuId={current} />;
};

export default MenuForm;
